/**
 * MonthPicker — popover-based month/year selector.
 *
 * Replaces the native `<input type='month'>` for activity period fields.
 * Click the trigger button → popover opens with a year header (← prev /
 * year label / next →) and a 3×4 grid of month buttons. Months in the
 * future and months before `min` (when supplied) are disabled.
 *
 * Value contract:
 *   - `value` and `onChange` use `'YYYY-MM'` strings, matching what the
 *     surrounding form expects (and what `expandMonthToPeriodRange()`
 *     consumes at submit time).
 *   - `''` means no selection.
 *
 * Constraints:
 *   - `min` (also `'YYYY-MM'`): months strictly before this are disabled.
 *     Used by the period-end picker to enforce `end >= start`.
 *   - The picker never lets users pick months past the current month —
 *     reporting periods can't include the future.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@g17eco/ui-modern/components/ui/popover';
import { cn } from '@g17eco/ui-modern/lib/utils';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const LONG_MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_RE = /^\d{4}-\d{2}$/;

interface ParsedMonth {
  year: number;
  /** 1-12 */
  month: number;
}

const parseMonth = (value: string): ParsedMonth | null => {
  if (!MONTH_RE.test(value)) {
    return null;
  }
  const [y, m] = value.split('-').map(Number);
  if (!y || !m || m < 1 || m > 12) {
    return null;
  }
  return { year: y, month: m };
};

const formatMonth = ({ year, month }: ParsedMonth): string =>
  `${year}-${String(month).padStart(2, '0')}`;

const formatDisplay = ({ year, month }: ParsedMonth): string =>
  `${LONG_MONTH_LABELS[month - 1]} ${year}`;

/** Returns true when `a` <= `b` (calendar-month comparison). */
const lte = (a: ParsedMonth, b: ParsedMonth): boolean =>
  a.year < b.year || (a.year === b.year && a.month <= b.month);

const todayParsed = (): ParsedMonth => {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
};

export interface MonthPickerProps {
  value: string;
  onChange: (value: string) => void;
  /** Lower bound (inclusive). `'YYYY-MM'`. */
  min?: string;
  /** Upper bound (inclusive). `'YYYY-MM'`. Defaults to the current month. */
  max?: string;
  /** Placeholder shown when no value is selected. */
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** ID used by the trigger button so the surrounding `<label>` can target it. */
  id?: string;
}

export const MonthPicker = ({
  value,
  onChange,
  min,
  max,
  placeholder = 'Select month',
  disabled,
  className,
  id,
}: MonthPickerProps) => {
  const minParsed = useMemo(() => (min ? parseMonth(min) : null), [min]);
  const maxParsed = useMemo(() => parseMonth(max ?? '') ?? todayParsed(), [max]);
  const valueParsed = useMemo(() => parseMonth(value), [value]);

  // Year currently shown in the popover. Defaults to the value's year, or
  // the max's year, or the current year. Reset whenever the popover
  // closes so re-opening starts on the relevant year.
  const [open, setOpen] = useState(false);
  const [shownYear, setShownYear] = useState<number>(
    valueParsed?.year ?? maxParsed.year,
  );

  // Sync shownYear to value when the popover opens — if the user had
  // navigated to 2018 but their value is 2024, we want 2024 next time.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setShownYear(valueParsed?.year ?? maxParsed.year);
    }
    wasOpenRef.current = open;
  }, [open, valueParsed, maxParsed.year]);

  // Year-arrow nav: disable forward when we'd cross max, back when we'd
  // cross min. Both are calendar-year comparisons; month bounds inside
  // the year are enforced per-button.
  const canPrevYear = !minParsed || shownYear - 1 >= minParsed.year;
  const canNextYear = shownYear + 1 <= maxParsed.year;

  const handlePick = (month: number) => {
    const next: ParsedMonth = { year: shownYear, month };
    onChange(formatMonth(next));
    setOpen(false);
  };

  const triggerLabel = valueParsed ? formatDisplay(valueParsed) : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          id={id}
          disabled={disabled}
          className={cn(
            'tw:flex tw:h-9 tw:w-full tw:items-center tw:justify-between tw:rounded-md tw:border tw:border-input tw:bg-transparent tw:px-3 tw:py-1 tw:text-sm tw:shadow-sm tw:outline-none',
            'tw:focus-visible:ring-1 tw:focus-visible:ring-ring',
            'tw:hover:bg-muted/30 tw:transition-colors',
            disabled && 'tw:opacity-50 tw:cursor-not-allowed',
            !valueParsed && 'tw:text-muted-foreground',
            className,
          )}
          aria-haspopup='dialog'
          aria-expanded={open}
        >
          <span>{triggerLabel}</span>
          <Calendar className='tw:size-4 tw:text-muted-foreground tw:shrink-0' />
        </button>
      </PopoverTrigger>

      <PopoverContent align='start' className='tw:rounded-lg tw:p-3 tw:w-[260px]'>
        <div className='tw:flex tw:items-center tw:justify-between tw:mb-3'>
          <Button
            type='button'
            variant='ghost'
            size='icon-sm'
            onClick={() => canPrevYear && setShownYear((y) => y - 1)}
            disabled={!canPrevYear}
            aria-label='Previous year'
          >
            <ChevronLeft className='tw:size-4' />
          </Button>
          <span className='tw:text-sm tw:font-semibold'>{shownYear}</span>
          <Button
            type='button'
            variant='ghost'
            size='icon-sm'
            onClick={() => canNextYear && setShownYear((y) => y + 1)}
            disabled={!canNextYear}
            aria-label='Next year'
          >
            <ChevronRight className='tw:size-4' />
          </Button>
        </div>

        <div className='tw:grid tw:grid-cols-3 tw:gap-1.5'>
          {MONTH_LABELS.map((label, idx) => {
            const month = idx + 1;
            const candidate: ParsedMonth = { year: shownYear, month };
            const beforeMin = minParsed ? !lte(minParsed, candidate) : false;
            const afterMax = !lte(candidate, maxParsed);
            const isDisabled = beforeMin || afterMax;
            const isSelected =
              valueParsed?.year === shownYear && valueParsed?.month === month;

            return (
              <Button
                variant={isSelected ? 'default' : isDisabled ? 'ghost' : 'light'}
                key={label}
                onClick={() => handlePick(month)}
                disabled={isDisabled}
                aria-pressed={isSelected}
              >
                {label}
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
